/**
 *  @class
 *  @function ScrollableContent
 */
if (!customElements.get('scrollable-content')) {
  class ScrollableContent extends HTMLElement {
    constructor() {
      super();
      this.container = null;
      this.items = [];
      this.prevButton = null;
      this.nextButton = null;
      this.currentIndex = 0;
      this.showArrows = this.dataset.showArrows === 'true';
      this.mobileScroll = this.dataset.mobileScroll === 'true';
    }

    connectedCallback() {
      this.container = this.querySelector('.scrollable-content--container');
      this.items = Array.from(this.querySelectorAll('.scrollable-content--item'));
      this.prevButton = this.querySelector('.scrollable-content--arrow-prev');
      this.nextButton = this.querySelector('.scrollable-content--arrow-next');

      if (!this.container || this.items.length === 0) {
        return;
      }

      // Initialize desktop slider with arrows
      if (this.showArrows && window.innerWidth >= 768) {
        this.initDesktopSlider();
      }

      // Initialize mobile scroll
      if (this.mobileScroll && window.innerWidth < 768) {
        this.initMobileScroll();
      }

      // Handle window resize
      window.addEventListener('resize', this.handleResize.bind(this));
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize.bind(this));
      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.handlePrev.bind(this));
      }
      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.handleNext.bind(this));
      }
    }

    initDesktopSlider() {
      if (!this.prevButton || !this.nextButton) {
        return;
      }

      // Add click event listeners
      this.prevButton.addEventListener('click', this.handlePrev.bind(this));
      this.nextButton.addEventListener('click', this.handleNext.bind(this));

      // Update button states
      this.updateButtonStates();

      // Add intersection observer for smoother transitions
      this.setupIntersectionObserver();
    }

    initMobileScroll() {
      // Add smooth scrolling behavior
      if (this.container) {
        this.container.style.scrollBehavior = 'smooth';
      }
    }

    handlePrev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.scrollToIndex(this.currentIndex);
      }
    }

    handleNext() {
      const itemsPerView = this.getItemsPerView();
      const maxIndex = Math.max(0, this.items.length - itemsPerView);

      if (this.currentIndex < maxIndex) {
        this.currentIndex++;
        this.scrollToIndex(this.currentIndex);
      }
    }

    scrollToIndex(index) {
      if (!this.items[index]) {
        return;
      }

      const item = this.items[index];
      const containerRect = this.container.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();

      // Calculate scroll position
      const scrollLeft = this.container.scrollLeft + (itemRect.left - containerRect.left);

      // Smooth scroll to position
      this.container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });

      this.updateButtonStates();
    }

    getItemsPerView() {
      const containerWidth = this.container.offsetWidth;
      const itemWidth = this.items[0]?.offsetWidth || 0;
      const gap = parseInt(getComputedStyle(this.container).gap) || 0;

      if (itemWidth === 0) return 1;

      return Math.floor((containerWidth + gap) / (itemWidth + gap));
    }

    updateButtonStates() {
      if (!this.prevButton || !this.nextButton) {
        return;
      }

      const itemsPerView = this.getItemsPerView();
      const maxIndex = Math.max(0, this.items.length - itemsPerView);

      // Update prev button
      if (this.currentIndex <= 0) {
        this.prevButton.disabled = true;
      } else {
        this.prevButton.disabled = false;
      }

      // Update next button
      if (this.currentIndex >= maxIndex) {
        this.nextButton.disabled = true;
      } else {
        this.nextButton.disabled = false;
      }
    }

    setupIntersectionObserver() {
      const observerOptions = {
        root: this.container,
        threshold: 0.5
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = this.items.indexOf(entry.target);
            if (index !== -1) {
              this.currentIndex = index;
              this.updateButtonStates();
            }
          }
        });
      }, observerOptions);

      this.items.forEach((item) => {
        observer.observe(item);
      });
    }

    handleResize() {
      const isDesktop = window.innerWidth >= 768;

      if (isDesktop && this.showArrows) {
        this.updateButtonStates();
      }
    }
  }

  customElements.define('scrollable-content', ScrollableContent);
}
